import { useState } from 'react';
import { SURFACE } from './common';

export interface DonutItem {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Texto extra na linha da legenda ("3 carregadores", "estimada"). */
  detail?: string;
}

const pct0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

function arcPath(cx: number, cy: number, r: number, inner: number, a0: number, a1: number): string {
  const pt = (rad: number, a: number) => [cx + rad * Math.sin(a), cy - rad * Math.cos(a)] as const;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = pt(r, a0);
  const [x1, y1] = pt(r, a1);
  const [x2, y2] = pt(inner, a1);
  const [x3, y3] = pt(inner, a0);
  return `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${inner},${inner} 0 ${large} 0 ${x3},${y3} Z`;
}

/**
 * Rosca de composição (AC/DC, receita por origem) — poucas fatias, sempre com
 * legenda rotulada (nome · valor · %) ao lado: a cor nunca é a única pista e o
 * número está lá sem hover. 2px de superfície entre as fatias. O centro mostra
 * o total, ou a fatia em foco (hover, toque ou teclado).
 */
export function Donut({
  items,
  formatValue,
  centerLabel,
  ariaLabel,
  size = 132
}: {
  items: DonutItem[];
  formatValue: (v: number) => string;
  centerLabel: string;
  ariaLabel: string;
  size?: number;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const total = items.reduce((a, it) => a + Math.max(it.value, 0), 0);
  if (total <= 0) {
    return <div className="py-8 text-center text-[13px] text-bow-royal/45">Sem dados no período.</div>;
  }

  const r = size / 2;
  const inner = r - 18;
  const vivos = items.filter((it) => it.value > 0);
  const foco = vivos.find((it) => it.key === hover) ?? null;
  let a = 0;

  return (
    <div className="flex items-center gap-5">
      <svg
        width={size}
        height={size}
        role="img"
        aria-label={`${ariaLabel}: ${vivos
          .map((it) => `${it.label} ${pct0.format((100 * it.value) / total)}%`)
          .join(', ')}`}
        className="shrink-0"
        onPointerLeave={() => setHover(null)}
      >
        {vivos.length === 1 ? (
          <circle cx={r} cy={r} r={(r + inner) / 2} fill="none" stroke={vivos[0]!.color} strokeWidth={r - inner} />
        ) : (
          vivos.map((it) => {
            const a0 = a;
            a += (2 * Math.PI * it.value) / total;
            return (
              <path
                key={it.key}
                d={arcPath(r, r, r, inner, a0, a)}
                fill={it.color}
                stroke={SURFACE}
                strokeWidth={2}
                opacity={hover === null || hover === it.key ? 1 : 0.5}
                onPointerEnter={() => setHover(it.key)}
                onPointerDown={() => setHover(it.key)}
              />
            );
          })
        )}
        <text x={r} y={r - 2} textAnchor="middle" fontSize={15} fontWeight={600} fill="#001B74">
          {formatValue(foco ? foco.value : total)}
        </text>
        <text x={r} y={r + 14} textAnchor="middle" fontSize={10.5} fill="rgba(0,27,116,0.5)">
          {foco ? foco.label : centerLabel}
        </text>
      </svg>
      <ul className="m-0 flex min-w-0 list-none flex-col gap-2.5 p-0 text-[13px]">
        {items.map((it) => (
          <li
            key={it.key}
            tabIndex={0}
            onFocus={() => setHover(it.key)}
            onBlur={() => setHover(null)}
            onPointerEnter={() => setHover(it.key)}
            onPointerLeave={() => setHover(null)}
            className="flex min-w-0 items-start gap-2 outline-none"
          >
            <span aria-hidden className="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: it.color }} />
            <span className="min-w-0">
              <span className="font-semibold text-bow-royal">{it.label}</span>
              <span className="tabular-nums text-bow-royal/60">
                {' '}
                · {formatValue(it.value)} · {pct0.format((100 * Math.max(it.value, 0)) / total)}%
              </span>
              {it.detail && <span className="block text-[11.5px] text-bow-royal/50">{it.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
